import { useEffect, useMemo, useRef, useState } from 'react'
import { Nfc, Smartphone, Search, ArrowDownAZ, ChevronRight, Trash2, UserPlus, Upload, ScanLine, Pencil, CircleCheck } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, useToast, apiGet, adminMutate } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import {
  useApi, useAccessCaps, ActionButton, runCommand, AccessModal,
  tr, CRED_STATUS, CRED_TYPE, RESULT_LABELS, TP_SOURCE, dateTime,
} from './lib.js'
import { useMenuAcces } from './menu-acces.js'

/**
 * IDENTIFIANTS (§61-83) — inventaire OPÉRABLE : recherche, tri, filtres,
 * langage humain (les énums provider restent en diagnostic), fiche par
 * identifiant (page réelle, pas de drawer §72), enrôlement et import réels en
 * Édition. Le token cryptographique ne quitte jamais le serveur.
 */

interface NfcRow { displayId: string | null; alias: string | null; nfcId: string | null; type: string | null; status: string | null; owner: string | null; ownerType: string | null }
interface TpRow { id: string; name: string | null; status: string | null; user: string | null; email: string | null; bundles: { source?: string; device_type?: string; status?: string }[] }

const FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'nfc', label: 'NFC' },
  { value: 'tp', label: 'Touch Pass' },
  { value: 'assigned', label: 'Assignés' },
  { value: 'unassigned', label: 'Non assignés' },
  { value: 'active', label: 'Actifs' },
]
const SORTS = [
  { value: 'owner', label: 'Titulaire' },
  { value: 'alias', label: 'Alias' },
  { value: 'type', label: 'Type' },
  { value: 'status', label: 'Statut' },
]

export function AccessCredentialsPage() {
  const meta = useShellMeta()
  const caps = useAccessCaps()
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [search, setSearch] = useState(q0.get('q') ?? '')
  const [filter, setFilter] = useState(q0.get('filtre') ?? 'all')
  const [sort, setSort] = useState(q0.get('tri') ?? 'owner')
  const [enrollOpen, setEnrollOpen] = useState(q0.get('enroler') === '1')
  const [importOpen, setImportOpen] = useState(false)
  const { data, reload } = useApi<{ nfc: NfcRow[]; touchPass: TpRow[] }>('/api/v1/access/credentials')

  useEffect(() => {
    const p = new URLSearchParams()
    if (search) p.set('q', search)
    if (filter !== 'all') p.set('filtre', filter)
    if (sort !== 'owner') p.set('tri', sort)
    window.history.replaceState(null, '', `/securite/controle-acces/identifiants${p.size ? `?${p}` : ''}`)
  }, [search, filter, sort])

  const s = search.trim().toLowerCase()
  const match = (...vals: (string | null)[]) => !s || vals.some((v) => (v ?? '').toLowerCase().includes(s))
  const nfc = (data?.nfc ?? [])
    .filter((c) => match(c.alias, c.owner, c.nfcId, c.displayId, c.type))
    .filter((c) => filter === 'all' || filter === 'nfc' || (filter === 'assigned' && !!c.owner) || (filter === 'unassigned' && !c.owner) || (filter === 'active' && c.status === 'ASSIGNED'))
    .filter(() => filter !== 'tp')
    .sort((a, b) => {
      if (sort === 'alias') return (a.alias ?? '').localeCompare(b.alias ?? '')
      if (sort === 'type') return (a.type ?? '').localeCompare(b.type ?? '')
      if (sort === 'status') return (a.status ?? '').localeCompare(b.status ?? '')
      return (a.owner ?? '﹏').localeCompare(b.owner ?? '﹏')
    })
  const tp = (data?.touchPass ?? [])
    .filter((t) => match(t.name, t.user, t.email))
    .filter(() => filter === 'all' || filter === 'tp' || filter === 'assigned' || filter === 'active')
    .filter((t) => filter !== 'assigned' || !!t.user)
    .filter((t) => filter !== 'active' || t.status === 'ACTIVATED' || t.status === 'ACTIVE')
    .sort((a, b) => (sort === 'status' ? (a.status ?? '').localeCompare(b.status ?? '') : (a.user ?? a.name ?? '').localeCompare(b.user ?? b.name ?? '')))
  const showNfc = filter !== 'tp'
  const showTp = filter === 'all' || filter === 'tp' || filter === 'assigned' || filter === 'active'

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <Trail />
        <header className="ac-head">
          <div><h1 className="lc-title">Identifiants</h1>
            <p className="lc-subtitle">Badges NFC, Touch Pass et PIN — le jeton cryptographique n’est jamais affiché.</p></div>
          <div className="ac-actions">
            <ActionButton caps={caps} action="ACCESS_NFC_ENROLL" label="Enrôler une carte" icon={<ScanLine size={14} aria-hidden="true" />} onRun={() => setEnrollOpen(true)} />
            <ActionButton caps={caps} action="ACCESS_NFC_IMPORT" label="Importer des cartes" icon={<Upload size={14} aria-hidden="true" />} onRun={() => setImportOpen(true)} />
          </div>
        </header>

        <div className="ac-toolbar">
          <label className="ac-searchwrap"><Search size={15} aria-hidden="true" />
            <input className="ac-search" placeholder="Alias, titulaire, UID, ID…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Rechercher un identifiant" />
          </label>
          <div className="ac-sortwrap"><ArrowDownAZ size={15} aria-hidden="true" />
            <Select value={sort} options={SORTS} onChange={setSort} ariaLabel="Trier les identifiants" />
          </div>
        </div>
        <div className="ac-quickfilters" role="tablist" aria-label="Filtres identifiants">
          {FILTERS.map((f) => <button key={f.value} type="button" role="tab" aria-selected={filter === f.value} className={`ac-qf ${filter === f.value ? 'is-on' : ''}`} onClick={() => setFilter(f.value)}>{f.label}</button>)}
        </div>

        {showNfc ? (
          <section className="lc-panel ac-plist-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Cartes NFC ({nfc.length})</h2></header>
            <div className="lc-panel__body lc-panel__body--flush">
              <ul className="ac-plist">
                {nfc.map((c, i) => (
                  <li key={i}>
                    <Link href={`/securite/controle-acces/identifiants/${c.displayId ?? c.nfcId}`} className="ac-prow ac-prow--cred">
                      <span className="ac-prow__id"><Nfc size={16} aria-hidden="true" className="ac-credicon" />
                        <span className="ac-prow__name"><strong>{c.alias || `Carte ${c.displayId}`}</strong><em>{tr(CRED_TYPE, c.type)} · ID {c.displayId}</em></span>
                      </span>
                      <span><span className={`ac-status ac-status--${(c.status ?? '').toLowerCase()}`}>{tr(CRED_STATUS, c.status)}</span></span>
                      <span className="ac-prow__owner">{c.owner ?? <em className="lc-hint">Non attribuée</em>}</span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
                {nfc.length === 0 ? <li className="lc-hint" style={{ padding: 'var(--sp-3) var(--sp-6)' }}>Aucune carte ne correspond.</li> : null}
              </ul>
            </div>
          </section>
        ) : null}

        {showTp ? (
          <section className="lc-panel ac-plist-panel">
            <header className="lc-panel__head"><h2 className="lc-section-title">Touch Pass ({tp.length})</h2></header>
            <div className="lc-panel__body lc-panel__body--flush">
              <ul className="ac-plist">
                {tp.map((t) => (
                  <li key={t.id}>
                    <Link href={`/securite/controle-acces/identifiants/tp:${t.id}`} className="ac-prow ac-prow--cred">
                      <span className="ac-prow__id"><Smartphone size={16} aria-hidden="true" className="ac-credicon" />
                        <span className="ac-prow__name"><strong>{t.name || 'Touch Pass'}</strong><em>{(t.bundles ?? []).map((b) => tr(TP_SOURCE, b.source ?? null)).filter(Boolean).join(', ') || 'Touch Pass'}</em></span>
                      </span>
                      <span><span className={`ac-status ac-status--${(t.status ?? '').toLowerCase()}`}>{tr(CRED_STATUS, t.status)}</span></span>
                      <span className="ac-prow__owner">{t.user ?? <em className="lc-hint">Non attribué</em>}</span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
                {tp.length === 0 ? <li className="lc-hint" style={{ padding: 'var(--sp-3) var(--sp-6)' }}>Aucun Touch Pass ne correspond.</li> : null}
              </ul>
            </div>
            <footer className="lc-panel__body ac-tpfoot">
              <span className="lc-hint">Achat de Touch Pass : différé — dépense réelle engagée, jamais exécuté sans lot dédié. Suspension/reprise : commande non implémentée (lot ultérieur).</span>
            </footer>
          </section>
        ) : null}

        {enrollOpen ? <EnrollModal onClose={(changed) => { setEnrollOpen(false); if (changed) setTimeout(reload, 1800) }} /> : null}
        {importOpen ? <ImportModal onClose={(changed) => { setImportOpen(false); if (changed) setTimeout(reload, 1800) }} /> : null}
      </main>
    </Shell>
  )
}

/* ─────────── FICHE IDENTIFIANT (§72-74) — page réelle ─────────── */

interface CredFiche {
  kind: 'NFC' | 'TOUCH_PASS'; id: string; alias?: string | null; name?: string | null; status: string | null; type?: string | null; note?: string | null
  owner: { id: string | null; name: string | null } | null; ownerType?: string | null; email?: string | null
  bundles?: { source?: string; device_type?: string; status?: string }[]
  activity?: { at: string | null; door: string | null; result: string | null }[]
  technical: Record<string, string | null>
}

export function AccessCredentialPage({ id }: { id: string }) {
  const meta = useShellMeta()
  const toast = useToast()
  const confirm = useConfirm()
  const caps = useAccessCaps()
  const menu = useMenuAcces()
  const { data: c, reload } = useApi<CredFiche>(`/api/v1/access/credentials/${encodeURIComponent(id)}`)
  const [busy, setBusy] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const tpId = c?.kind === 'TOUCH_PASS' ? c.id.slice(3) : null

  const deleteCard = async () => {
    if (!c || c.kind !== 'NFC') return
    if (!(await confirm({ title: `Supprimer la carte « ${c.alias || c.id} » ?`, message: `Suppression DÉFINITIVE de la carte dans UniFi Access.${c.owner?.name ? ` Elle est actuellement attribuée à ${c.owner.name}.` : ''}`, variant: 'critical', confirmLabel: 'Supprimer' }))) return
    setBusy(true)
    const r = await runCommand('ACCESS_NFC_DELETE', { card_id: c.id })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Carte supprimée dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  const unassignCard = async () => {
    if (!c || c.kind !== 'NFC' || !c.owner?.id) return
    if (!(await confirm({ title: 'Désattribuer ce badge ?', message: `La carte « ${c.alias || c.id} » n’ouvrira plus pour ${c.owner.name}. Elle reste dans l’inventaire.`, confirmLabel: 'Désattribuer' }))) return
    setBusy(true)
    const r = await runCommand('ACCESS_NFC_UNASSIGN', { user_id: c.owner.id, card_id: c.id })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Carte désattribuée dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  const tpSetStatus = async (status: 'ACTIVE' | 'SUSPENDED') => {
    if (!tpId || !c) return
    if (!(await confirm({ title: `${status === 'SUSPENDED' ? 'Suspendre' : 'Réactiver'} ce Touch Pass ?`, message: status === 'SUSPENDED' ? `« ${c.name || tpId} » n’ouvrira plus jusqu’à sa réactivation.` : `« ${c.name || tpId} » ouvrira à nouveau.`, variant: 'sensitive', confirmLabel: status === 'SUSPENDED' ? 'Suspendre' : 'Réactiver' }))) return
    setBusy(true)
    const r = await runCommand('ACCESS_TOUCHPASS_UPDATE', { touch_pass_id: tpId, status })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', status === 'SUSPENDED' ? 'Touch Pass suspendu dans UniFi Access.' : 'Touch Pass réactivé dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  const tpUnassign = async () => {
    if (!tpId || !c?.owner?.id) return
    if (!(await confirm({ title: 'Désattribuer ce Touch Pass ?', message: `Il n’ouvrira plus pour ${c.owner.name}.`, confirmLabel: 'Désattribuer' }))) return
    setBusy(true)
    const r = await runCommand('ACCESS_TOUCHPASS_UNASSIGN', { user_id: c.owner.id, touch_pass_id: tpId })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Touch Pass désattribué dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <SmartBack fallback="/securite/controle-acces/identifiants" />
        {c ? (
          <>
            <header className="ac-head">
              <div className="ac-phead__id">
                <span className="ac-avatar ac-avatar--lg ac-avatar--cred">{c.kind === 'NFC' ? <Nfc size={22} aria-hidden="true" /> : <Smartphone size={22} aria-hidden="true" />}</span>
                <div>
                  <h1 className="lc-title">{c.kind === 'NFC' ? (c.alias || `Carte ${c.id}`) : (c.name || 'Touch Pass')}</h1>
                  <p className="lc-subtitle ac-phead__meta">
                    <span className={`ac-status ac-status--${(c.status ?? '').toLowerCase()}`}>{tr(CRED_STATUS, c.status)}</span>
                    <span className="ac-typechip">{c.kind === 'NFC' ? tr(CRED_TYPE, c.type ?? null) : 'Touch Pass'}</span>
                  </p>
                </div>
              </div>
              {c.kind === 'NFC' ? (
                <div className="ac-actions">
                  <ActionButton caps={caps} action="ACCESS_NFC_UPDATE" label="Renommer" icon={<Pencil size={14} aria-hidden="true" />} onRun={() => setRenameOpen(true)} />
                  {c.owner?.id ? <ActionButton caps={caps} action="ACCESS_NFC_UNASSIGN" label="Désattribuer" busy={busy} onRun={() => void unassignCard()} /> : null}
                  <ActionButton caps={caps} action="ACCESS_NFC_DELETE" label="Supprimer la carte" icon={<Trash2 size={14} aria-hidden="true" />} busy={busy} onRun={() => void deleteCard()} />
                </div>
              ) : (
                <div className="ac-actions">
                  {c.status === 'SUSPENDED'
                    ? <ActionButton caps={caps} action="ACCESS_TOUCHPASS_UPDATE" label="Réactiver" primary busy={busy} onRun={() => void tpSetStatus('ACTIVE')} />
                    : <ActionButton caps={caps} action="ACCESS_TOUCHPASS_UPDATE" label="Suspendre" busy={busy} onRun={() => void tpSetStatus('SUSPENDED')} />}
                  {c.owner?.id ? <ActionButton caps={caps} action="ACCESS_TOUCHPASS_UNASSIGN" label="Désattribuer" busy={busy} onRun={() => void tpUnassign()} /> : null}
                </div>
              )}
            </header>

            <div className="ac-detail-grid">
              <section className="ac-panel">
                <h2 className="lc-section-title">Titulaire</h2>
                {c.owner?.id ? (
                  <Link href={`/securite/controle-acces/personnes/${c.owner.id}`} className="ac-ownerlink" {...menu.personne({ id: c.owner.id, nom: c.owner.name })}><UserPlus size={14} aria-hidden="true" /> {c.owner.name} <ChevronRight size={13} aria-hidden="true" /></Link>
                ) : c.owner?.name ? <p>{c.owner.name}</p> : <p className="lc-hint">Non attribué.</p>}
                {c.email ? <p className="lc-hint">{c.email}</p> : null}
              </section>

              {c.kind === 'TOUCH_PASS' ? (
                <section className="ac-panel">
                  <h2 className="lc-section-title">Appareils</h2>
                  <ul className="ac-bundles">
                    {(c.bundles ?? []).map((b, i) => <li key={i}><Smartphone size={13} aria-hidden="true" /> {tr(TP_SOURCE, b.source ?? null)}{b.device_type ? ` · ${b.device_type}` : ''}{b.status ? ` · ${tr(CRED_STATUS, b.status)}` : ''}</li>)}
                    {(c.bundles ?? []).length === 0 ? <li className="lc-hint">Aucun appareil associé.</li> : null}
                  </ul>
                </section>
              ) : (
                <section className="ac-panel">
                  <h2 className="lc-section-title">Activité NFC du titulaire</h2>
                  <ul className="ac-events">
                    {(c.activity ?? []).map((e, i) => <li key={i} className={`ac-event ${e.result && e.result !== 'ACCESS' ? 'ac-event--denied' : ''}`}><span className="ac-event__time">{dateTime(e.at)}</span><span className="ac-event__door">{e.door ?? '—'}</span><span className="ac-event__result">{tr(RESULT_LABELS, e.result)}</span></li>)}
                    {(c.activity ?? []).length === 0 ? <li className="lc-hint">Aucune activité NFC.</li> : null}
                  </ul>
                  <p className="lc-hint">L’historique provider ne référence pas chaque carte individuellement : activité NFC du titulaire.</p>
                </section>
              )}

              <section className="ac-panel">
                <h2 className="lc-section-title">Diagnostic</h2>
                <dl className="ac-dl">
                  {Object.entries(c.technical).map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="ac-mono">{v ?? '—'}</dd></div>)}
                </dl>
                <p className="lc-hint">Valeurs provider brutes — le langage humain est utilisé partout ailleurs.</p>
              </section>
            </div>
            {renameOpen && c.kind === 'NFC' ? <RenameCardModal card={c} onClose={(changed) => { setRenameOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
          </>
        ) : <p className="lc-hint">Chargement…</p>}
      </main>
    </Shell>
  )
}

/** RENOMMER une carte (§6.10) — alias humain, provider inchangé par ailleurs. */
function RenameCardModal({ card, onClose }: { card: CredFiche; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [alias, setAlias] = useState(card.alias ?? '')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    const r = await runCommand('ACCESS_NFC_UPDATE', { card_id: card.id, alias })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Carte renommée dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={`Renommer — ${card.alias || card.id}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <label className="ac-field">Alias de la carte<input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Ex. Badge vestiaire 3" /></label>
    </AccessModal>
  )
}

/* ── ENRÔLEMENT Companion V1 (§50-58) — session AVANT provider, TOUJOURS ──
   Ordre absolu (§8/§60) : 1 session locale Gestion → 2 attente lecteur →
   3 UID détecté → 4 alias + titulaire → 5 validation → 6 SEULEMENT ALORS
   import officiel UniFi Access (attribution chaînée côté worker). */

interface EnrollDevice { id: string; name: string; alias: string; type: string; online: boolean; enrollCapable: boolean }
interface NfcStation { id: string; name: string; reader: string | null; online: boolean; revoked: boolean }
interface OwnerHit { id: string; fullName: string; status: string }

function EnrollModal({ onClose }: { onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const { data } = useApi<{ devices: EnrollDevice[] }>('/api/v1/access/devices')
  const { data: stationsData } = useApi<{ stations: NfcStation[] }>('/api/v1/access/nfc-stations')
  const readers = (data?.devices ?? []).filter((d) => d.online && d.enrollCapable)
  const stations = (stationsData?.stations ?? []).filter((st) => !st.revoked)

  type Phase = 'choose' | 'pick-unifi' | 'pick-pc' | 'waiting' | 'detected' | 'done'
  const [phase, setPhase] = useState<Phase>('choose')
  const [device, setDevice] = useState('')
  const [station, setStation] = useState<NfcStation | null>(null)
  const [uid, setUid] = useState<string | null>(null)
  const [alias, setAlias] = useState('')
  const [owner, setOwner] = useState<OwnerHit | null>(null)
  const [ownerQuery, setOwnerQuery] = useState('')
  const [ownerHits, setOwnerHits] = useState<OwnerHit[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [unifiCard, setUnifiCard] = useState<Record<string, unknown> | null>(null)
  const sessionRef = useRef<string | null>(null)

  // Fermeture en pleine attente : la session PC est annulée proprement (§32).
  useEffect(() => () => { if (sessionRef.current) void adminMutate('DELETE', `/api/v1/access/nfc-sessions/${sessionRef.current}`, {}) }, [])

  // Recherche de titulaire (§54) — personnes Access, après détection seulement.
  useEffect(() => {
    if (phase !== 'detected' || ownerQuery.trim().length < 2) { setOwnerHits([]); return }
    const t = setTimeout(() => {
      void apiGet<{ people: { id: string; fullName: string; status: string }[] }>(
        `/api/v1/access/people?search=${encodeURIComponent(ownerQuery.trim())}`,
      ).then((r) => setOwnerHits((r.people ?? []).slice(0, 6).map((p) => ({ id: p.id, fullName: p.fullName, status: p.status })))).catch(() => {})
    }, 250)
    return () => clearTimeout(t)
  }, [ownerQuery, phase])

  /** Flux LECTEUR UNIFI : session provider réelle sur le lecteur mural. */
  const startUnifi = async () => {
    setPhase('waiting'); setError(null)
    const r = await runCommand('ACCESS_NFC_ENROLL', { device_id: device }, { timeoutMs: 120_000 })
    if (!r.ok) { setError(r.error); setPhase('pick-unifi'); return }
    const card = (r.result as { card?: Record<string, unknown> } | undefined)?.card ?? null
    setUnifiCard(card)
    setUid(String(card?.display_id ?? card?.id ?? '')) 
    setPhase('done')
    toast('ok', 'Carte enrôlée dans UniFi Access via le lecteur.')
  }

  /** Flux LECTEUR DE CE PC (§52-53) : session LOCALE — AUCUN appel UniFi ici (§120). */
  const startPc = async () => {
    if (!station) return
    setPhase('waiting'); setError(null)
    const c = await adminMutate<{ sessionId?: string; expiresAt?: string; message?: string; error?: string }>('POST', '/api/v1/access/nfc-sessions', { stationId: station.id })
    if (c.status >= 400 || !c.data.sessionId) { setError(c.data.message ?? c.data.error ?? 'Session refusée.'); setPhase('pick-pc'); return }
    sessionRef.current = c.data.sessionId
    const expires = c.data.expiresAt ? new Date(c.data.expiresAt).getTime() : Date.now() + 120_000
    while (sessionRef.current) {
      setSecondsLeft(Math.max(0, Math.round((expires - Date.now()) / 1000)))
      await new Promise((res) => setTimeout(res, 900))
      if (!sessionRef.current) return
      const st = await apiGet<{ status: string; uid: string | null }>(`/api/v1/access/nfc-sessions/${sessionRef.current}`)
      if (st.status === 'DETECTED' && st.uid) {
        sessionRef.current = null
        setUid(st.uid)
        setPhase('detected')          // ← RIEN n'est parti vers UniFi Access (§57)
        return
      }
      if (st.status === 'EXPIRED') { sessionRef.current = null; setError('Aucune carte présentée avant l’expiration.'); setPhase('pick-pc'); return }
      if (st.status === 'CANCELLED') { sessionRef.current = null; setError('Session annulée depuis le PC.'); setPhase('pick-pc'); return }
    }
  }

  const cancelWait = async () => {
    const id = sessionRef.current
    sessionRef.current = null
    if (id) await adminMutate('DELETE', `/api/v1/access/nfc-sessions/${id}`, {})
    setPhase(station ? 'pick-pc' : 'pick-unifi')
  }

  /** §55-58 : LE seul appel provider — import officiel + attribution chaînée
   *  côté worker. Une erreur laisse l'UID et le formulaire INTACTS (§58). */
  const createCard = async () => {
    if (!uid) return
    setBusy(true); setError(null)
    const payload: Record<string, unknown> = { cards: [{ nfc_id: uid, alias: alias.trim() || undefined }] }
    if (owner) payload.assign_user_id = owner.id
    const r = await runCommand('ACCESS_NFC_IMPORT', payload)
    setBusy(false)
    if (!r.ok) { setError(r.error); return } // pas de rescan : corriger et réessayer
    setPhase('done')
    toast('ok', owner ? `Carte créée et attribuée à ${owner.fullName}.` : 'Carte créée dans UniFi Access.')
  }

  const uidPretty = uid && /^[0-9A-F]+$/.test(uid) ? (uid.match(/.{2}/g) ?? [uid]).join(' ') : uid

  return (
    <AccessModal title="Enrôler une carte NFC" onClose={() => onClose(phase === 'done')} footer={
      phase === 'choose' ? <button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
      : phase === 'pick-unifi' ? (
        <><button type="button" className="lc-btn" onClick={() => setPhase('choose')}>Retour</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!device} onClick={() => void startUnifi()}>Démarrer la session</button></>
      ) : phase === 'pick-pc' ? (
        <><button type="button" className="lc-btn" onClick={() => setPhase('choose')}>Retour</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!station?.online} onClick={() => void startPc()}>Attendre une carte</button></>
      ) : phase === 'waiting' ? <button type="button" className="lc-btn" onClick={() => void cancelWait()}>Annuler</button>
      : phase === 'detected' ? (
        <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Abandonner</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void createCard()}>
            {busy ? 'Création…' : owner ? 'Créer et attribuer' : 'Créer la carte'}
          </button></>
      ) : <button type="button" className="lc-btn lc-btn--primary" onClick={() => onClose(true)}>Terminer</button>
    }>
      {phase === 'choose' ? (
        <>
          <p className="lc-hint">Choisissez comment lire la carte.</p>
          <div className="ac-enroll-choices">
            <button type="button" className="ac-enroll-choice" onClick={() => setPhase('pick-unifi')}>
              <Nfc size={22} aria-hidden="true" />
              <strong>Lecteur UniFi</strong>
              <span>Un lecteur mural du club ({readers.length} disponible{readers.length > 1 ? 's' : ''})</span>
            </button>
            <button type="button" className="ac-enroll-choice" onClick={() => setPhase('pick-pc')}>
              <ScanLine size={22} aria-hidden="true" />
              <strong>Lecteur de ce PC</strong>
              <span>Un lecteur USB via Gestion NFC Companion ({stations.filter((st) => st.online).length} en ligne)</span>
            </button>
          </div>
        </>
      ) : phase === 'pick-unifi' ? (
        <>
          <p className="lc-hint">Choisir le lecteur mural, démarrer la session, puis présenter la carte. UniFi Access confirme la détection.</p>
          {readers.length > 0
            ? <Select value={device} options={readers.map((d) => ({ value: d.id, label: `${d.alias || d.name} (${d.type})` }))} onChange={setDevice} placeholder="Choisir un lecteur…" ariaLabel="Lecteur d’enrôlement" />
            : <p className="ac-blockhint">Aucun lecteur mural en ligne capable d’enrôler.</p>}
          {error ? <p className="ac-err">{error}</p> : null}
        </>
      ) : phase === 'pick-pc' ? (
        <>
          {stations.length === 0 ? (
            <p className="ac-blockhint">Aucune station NFC appairée. Installer Gestion NFC Companion (Administration → Intégrations → UniFi Access → Stations NFC).</p>
          ) : (
            <div className="ac-stationpick">
              {stations.map((st) => (
                <button key={st.id} type="button" disabled={!st.online}
                  className={`ac-stationcard ${station?.id === st.id ? 'is-on' : ''} ${st.online ? '' : 'is-off'}`}
                  onClick={() => setStation(st)}>
                  <span className={`lc-dot ${st.online ? 'lc-dot--ok' : 'lc-dot--idle'}`} aria-hidden="true" />
                  <strong>{st.name}</strong>
                  <span>{st.reader ?? 'Lecteur inconnu'}</span>
                  <em>{st.online ? 'En ligne · prêt' : 'Hors ligne'}</em>
                </button>
              ))}
            </div>
          )}
          <p className="lc-hint">Le PC ne détient aucun jeton UniFi : il lit l’UID, Gestion fait le reste.</p>
          {error ? <p className="ac-err">{error}</p> : null}
        </>
      ) : phase === 'waiting' ? (
        <div className="ac-enroll-wait">
          <ScanLine size={34} aria-hidden="true" className="ac-spin" />
          <p><strong>Présentez la carte sur {station ? (station.reader ?? station.name) : 'le lecteur'}…</strong></p>
          <p className="lc-hint">{station ? 'La station remonte uniquement l’UID à Gestion — aucun appel UniFi Access à ce stade.' : 'La détection est confirmée par UniFi Access.'}</p>
          {secondsLeft != null && station ? <p className="lc-hint">Expire dans {secondsLeft} s</p> : null}
        </div>
      ) : phase === 'detected' ? (
        <>
          <div className="ac-enroll-detected">
            <CircleCheck size={20} aria-hidden="true" />
            <div><strong>Carte détectée</strong><span className="ac-mono ac-enroll-uid">{uidPretty}</span></div>
          </div>
          <label className="ac-field">Alias (optionnel)<input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Ex. Badge vestiaire 3" /></label>
          <label className="ac-field">Titulaire (optionnel)
            <input value={owner ? owner.fullName : ownerQuery}
              onChange={(e) => { setOwner(null); setOwnerQuery(e.target.value) }}
              placeholder="Rechercher une personne…" />
          </label>
          {!owner && ownerHits.length > 0 ? (
            <ul className="ac-ownerhits">
              {ownerHits.map((h) => (
                <li key={h.id}><button type="button" onClick={() => { setOwner(h); setOwnerHits([]) }}>{h.fullName}{h.status !== 'ACTIVE' ? ' (inactive)' : ''}</button></li>
              ))}
            </ul>
          ) : null}
          {owner ? <p className="lc-hint">La carte sera attribuée à <strong>{owner.fullName}</strong> — <button type="button" className="ac-inline-link" onClick={() => { setOwner(null); setOwnerQuery('') }}>retirer</button></p> : null}
          {error ? <p className="ac-err">{error} La carte détectée est conservée — corrigez puis réessayez, sans rescanner.</p> : null}
        </>
      ) : (
        <div className="ac-enroll-wait">
          <Nfc size={34} aria-hidden="true" />
          <p><strong>{unifiCard ? 'Carte enrôlée.' : owner ? 'Carte créée et attribuée.' : 'Carte créée.'}</strong></p>
          {uidPretty ? <p className="lc-hint">UID <span className="ac-mono">{uidPretty}</span></p> : null}
        </div>
      )}
    </AccessModal>
  )
}

/* ─────────── IMPORT (§76, §79) — CSV nfc_id[,alias] avec aperçu ─────────── */

function ImportModal({ onClose }: { onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rows = text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const [nfcId, alias] = l.split(/[;,\t]/).map((x) => x?.trim())
    return { nfc_id: nfcId ?? '', alias: alias || undefined }
  }).filter((r) => r.nfc_id)

  const run = async () => {
    setBusy(true); setError(null)
    const r = await runCommand('ACCESS_NFC_IMPORT', { cards: rows })
    setBusy(false)
    if (!r.ok) { setError(r.error); return }
    toast('ok', `${rows.length} carte(s) importée(s) dans UniFi Access.`)
    onClose(true)
  }

  return (
    <AccessModal title="Importer des cartes NFC" onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={rows.length === 0 || busy} onClick={() => void run()}>{busy ? 'Import…' : `Importer ${rows.length} carte(s)`}</button></>
    }>
      <p className="lc-hint">Une carte par ligne : <code>UID</code> ou <code>UID,alias</code> (cartes tierces déjà encodées). Aperçu avant validation.</p>
      <textarea className="ac-import-ta" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={'53D6E562,Badge vestiaire 1\nC3B4EC62'} />
      {rows.length > 0 ? (
        <ul className="ac-import-preview">{rows.slice(0, 8).map((r, i) => <li key={i}><span className="ac-mono">{r.nfc_id}</span>{r.alias ? ` — ${r.alias}` : ''}</li>)}{rows.length > 8 ? <li className="lc-hint">… et {rows.length - 8} de plus</li> : null}</ul>
      ) : null}
      {error ? <p className="ac-err">{error}</p> : null}
    </AccessModal>
  )
}
